import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import { FullViewSpinner } from '@provider-portal/common/components/FullViewSpinner/FullViewSpinner';
import { useOnNavigateIn } from '@provider-portal/common/hooks/useOnNavigateIn';
import { AlertIcon } from '@provider-portal/common/icons/AlertIcon';
import { SuccessIcon } from '@provider-portal/common/icons/SuccessIcon';
import * as qs from 'query-string';

import { useAppServices } from '../../components/AppServicesProvider';

import { $tk } from 'utils/appTranslationKey';
import { useLocalizedDocumentTitle } from 'root/navigation/useLocalizedDocumentTitle';

interface EmailConfirmationMessageProps {
  isSuccess: boolean;
  isError: boolean;
  onClick: () => void;
}

const queryStringObject = qs.parse(window.location.search);
const unlockAccountToken = queryStringObject?.token?.toString();

const confirmIconStyle = {
  width: '7em',
  height: '7em',
};

const AccountUnlockMessage: React.FC<EmailConfirmationMessageProps> = ({
  isSuccess,
  isError,
  onClick,
}) => {
  const { formatMessage } = useIntl();

  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  const confirmStatus = useMemo(() => {
    if (isSuccess) {
      return (
        <Box>
          <Box>
            <SuccessIcon style={confirmIconStyle} />
          </Box>
          <Box mt={2}>
            <Typography variant="h5">
              {formatMessage({ id: $tk.screens.AccountUnlock.unlockSuccess })}
            </Typography>
          </Box>
          <Box mt={10} display="flex" justifyContent="center">
            <Button variant="contained" onClick={handleClick}>
              {formatMessage({ id: $tk.screens.AccountUnlock.goToLogin })}
            </Button>
          </Box>
        </Box>
      );
    }
    if (isError) {
      return (
        <Box>
          <Box>
            <AlertIcon style={confirmIconStyle} />
          </Box>
          <Box mt={2}>
            <Typography variant="h5">
              {formatMessage({ id: $tk.screens.AccountUnlock.unlockFail })}
            </Typography>
          </Box>
          <Box mt={10} display="flex" justifyContent="center">
            <Button variant="contained" onClick={handleClick}>
              {formatMessage({ id: $tk.screens.AccountUnlock.goToLogin })}
            </Button>
          </Box>
        </Box>
      );
    }
    return null;
  }, [formatMessage, handleClick, isError, isSuccess]);

  return (
    <Box
      textAlign="center"
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      {confirmStatus}
    </Box>
  );
};

export const AccountUnlock = () => {
  useLocalizedDocumentTitle($tk.screens.AccountUnlock.title);

  const [isLoaderVisible, setLoaderVisible] = useState<boolean>(true);
  const [isSuccess, setSuccess] = useState<boolean>(false);
  const [isError, setError] = useState<boolean>(false);
  const history = useHistory();
  const { apiClient } = useAppServices();

  const handleOnClick = useCallback(() => {
    history.push('/');
  }, [history]);

  const sendUnlockAccountRequest = useCallback(async () => {
    try {
      await apiClient.deliveryProviderNoAuth.publicUnlockAccount({
        token: unlockAccountToken,
      });
      setSuccess(true);
      setError(false);
    } catch (_error) {
      setSuccess(false);
      setError(true);
    }
    setLoaderVisible(false);
  }, [apiClient.deliveryProviderNoAuth]);

  useOnNavigateIn(sendUnlockAccountRequest);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <Box sx={{ maxWidth: '450px', width: '85vw' }}>
        {isLoaderVisible ? (
          <FullViewSpinner />
        ) : (
          <AccountUnlockMessage isError={isError} isSuccess={isSuccess} onClick={handleOnClick} />
        )}
      </Box>
    </Box>
  );
};
